import { Children, Fragment, isValidElement, memo, ReactNode } from 'react';
import { HiXMark } from 'react-icons/hi2';
import { DataTableFilterKind, IDataTableColumnFilter } from '../DataTable.types';
import { describeColumnFilter } from '../dataTableFilters';
import type { DataTableFacetLabels } from '../useDataTableFacetLabels';

export interface IDataTableFilterSummaryColumn {
    id: string;
    header: string;
    kind: DataTableFilterKind;
}

interface DataTableFilterSummaryProps {
    columns: IDataTableFilterSummaryColumn[];
    extra?: ReactNode;
    end?: ReactNode;
    filters: { id: string; value: unknown }[];
    /** Подписи значений header-фильтра по ключу; без подписи в чипе ключ. */
    facetLabels?: DataTableFacetLabels;
    globalFilter: string;
    onRemove: (columnId: string) => void;
    onClearSearch: () => void;
    onClearAll: () => void;
}

/** Пустой массив, пустая строка и фрагмент без детей — тоже ReactNode, но условий в них нет. Компонент, который
 *  сам решает ничего не рисовать, отсюда не виден: такой `extra` передают как `null`. */
const hasContent = (node: ReactNode): boolean =>
    Children.toArray(node).some((child) =>
        isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment
            ? hasContent(child.props.children)
            : child !== '',
    );

/**
 * Строка активных фильтров (аналог FilterPanel DevExtreme): какие условия сейчас сужают
 * список. Нужна там, где фильтр задан не руками в строке фильтров — пришёл по ссылке
 * или из кода, и без неё его не видно.
 */
const DataTableFilterSummary = memo(
    ({
        columns,
        extra,
        end,
        filters,
        facetLabels,
        globalFilter,
        onRemove,
        onClearSearch,
        onClearAll,
    }: DataTableFilterSummaryProps) => {
        const chips = filters
            .map((filter) => {
                const column = columns.find((c) => c.id === filter.id);
                if (!column) return null;
                const labels = facetLabels?.[filter.id];
                const text = describeColumnFilter(
                    filter.value as IDataTableColumnFilter,
                    column.kind,
                    labels && ((key) => labels[key]),
                );
                return text ? { id: filter.id, header: column.header, text } : null;
            })
            .filter((chip): chip is { id: string; header: string; text: string } => chip !== null);
        const hasFilters = !!chips.length || !!globalFilter || hasContent(extra);
        if (!hasFilters && !end) return null;
        return (
            <div className="dtb__fsummary" role="group" aria-label="Активные фильтры" data-testid="dtb-filter-summary">
                <div className="dtb__fsummary-items">
                    {extra}
                    {chips.map((chip) => (
                        <span key={chip.id} className="dtb__fsummary-chip">
                            <span className="dtb__fsummary-text">
                                <b>{chip.header}:</b> {chip.text}
                            </span>
                            <button
                                type="button"
                                className="dtb__fsummary-remove"
                                aria-label={`Снять фильтр: ${chip.header.toLocaleLowerCase('ru-RU')}`}
                                onClick={() => onRemove(chip.id)}
                            >
                                <HiXMark aria-hidden />
                            </button>
                        </span>
                    ))}
                    {globalFilter && (
                        <span className="dtb__fsummary-chip">
                            <span className="dtb__fsummary-text">
                                <b>Поиск:</b> «{globalFilter}»
                            </span>
                            <button
                                type="button"
                                className="dtb__fsummary-remove"
                                aria-label="Снять поиск"
                                onClick={onClearSearch}
                            >
                                <HiXMark aria-hidden />
                            </button>
                        </span>
                    )}
                    {hasFilters && (
                        <button type="button" className="dtb__fsummary-clear" onClick={onClearAll}>
                            Сбросить все
                        </button>
                    )}
                </div>
                {end && <span className="dtb__fsummary-end">{end}</span>}
            </div>
        );
    },
);
DataTableFilterSummary.displayName = 'DataTableFilterSummary';

export default DataTableFilterSummary;
